"use client";

import { useLazyQuery, useQuery } from "@apollo/client/react";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useStartViewTransitionState } from "use-start-view-transition";

import { PollWeightType } from "@/backend/focus";
import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { useIsLoggedIn } from "@/components/shared/is-logged-in";
import { Spinner } from "@/components/shared/spinner";
import { StopPropagationWrapper } from "@/components/shared/stop-propagation-wrapper";
import { Button } from "@/components/ui/button";
import { usePostCacheContext } from "@/contexts/post-cache-context";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import {
  PostPollAssociationsDocument,
  TokenBalancesDocument,
} from "@/graphql/codegen/graphql";
import { DESO_APP_NAME, DESO_TOKEN_NAME } from "@/utils/constants";
import { DIALOG } from "@/utils/dialog-routes";
import type { Dictionary } from "@/utils/helpers";
import { isAbortError, pluralize } from "@/utils/helpers";

import { normalizePollOptions } from "./poll-options";
import { PostActivePoll } from "./post-active-poll";
import type { PollResult } from "./post-poll-results";
import { PostPollResults } from "./post-poll-results";

export const PostPoll = ({ extraData }: { extraData: Dictionary<string> }) => {
  const {
    user,
    realTimeDesoBalanceNanos,
    refetchUser,
    userLoading,
    currentUserPublicKey,
  } = useContext(UserContext);
  const pollType = extraData["PollWeightType"] as PollWeightType;
  const PollOptions = extraData["PollOptions"];
  const PollWeightTokenPublicKey = extraData["PollWeightTokenPublicKey"] ?? "";
  const pollContainerRef = useRef<HTMLDivElement>(null!);
  const { post, repost, isPureRepost, postCachePrefix } = usePostContext();
  const [showResultsOnly, setShowResultsOnly] =
    useStartViewTransitionState(false);

  const postHash = isPureRepost ? repost?.postHash : post?.postHash;
  const isCurrentUserAuthor =
    (isPureRepost ? repost : post)?.poster?.publicKey === currentUserPublicKey;

  const isLoggedIn = useIsLoggedIn();

  if (!postHash) {
    throw new Error("Post hash is missing.");
  }

  const {
    resizeObserver,
    pollHeight = 0,
    postCacheKey,
  } = usePostCacheContext(postHash, postCachePrefix);

  useEffect(() => {
    const node = pollContainerRef.current;
    resizeObserver.observe(node);

    return () => {
      resizeObserver.unobserve(node);
    };
  }, [pollContainerRef.current]);

  const [
    geTokenBalance,
    { data: tokenBalancesData, loading: tokenBalancesLoading },
  ] = useLazyQuery(TokenBalancesDocument);

  const {
    data: pollResultsData,
    loading: pollResultsLoading,
    refetch: refetchPollResults,
  } = useQuery(PostPollAssociationsDocument, {
    variables: {
      postHash,
      includeTokenBalances: !!PollWeightTokenPublicKey,
      tokenBalancesFilter: {
        creator: {
          publicKey: {
            equalTo: PollWeightTokenPublicKey,
          },
        },
      },
    },
  });

  const { currentUserVote, results } = useMemo(() => {
    const results =
      pollResultsData?.postByPostHash?.postAssociationsByPostHash?.nodes
        .filter(Boolean)
        .map((x) => {
          return {
            value: x?.associationValue,
            transactor: x?.transactor,
          };
        }) || [];

    const currentUserVote =
      results?.find((x) => x.transactor?.publicKey === currentUserPublicKey) ??
      null;

    return {
      results,
      currentUserVote,
    };
  }, [
    pollResultsData?.postByPostHash?.postAssociationsByPostHash?.nodes,
    currentUserPublicKey,
  ]);

  const availableToVote = useMemo(() => {
    const shouldVoteByTokenBalance = pollType !== PollWeightType.unweighted;

    if (!isLoggedIn) {
      return false;
    }

    if (!shouldVoteByTokenBalance) {
      return true;
    }

    let tokenBalance = 0;
    if (pollType === PollWeightType.desoTokenBalance) {
      tokenBalance = Number(
        tokenBalancesData?.tokenBalances?.nodes[0]?.balanceNanos ?? "0",
      );
    } else if (pollType === PollWeightType.desoBalance) {
      tokenBalance = realTimeDesoBalanceNanos;
    }

    return tokenBalance > 0;
  }, [
    realTimeDesoBalanceNanos,
    pollType,
    tokenBalancesData?.tokenBalances?.nodes,
  ]);

  const groupedResults = useMemo(() => {
    return results.reduce<Record<string, PollResult[]>>((acc, curr) => {
      if (!curr?.value) return acc;

      acc[curr.value] = [...(acc[curr.value] || []), curr];
      return acc;
    }, {});
  }, [results]);
  const defaultPollVotersTab = Object.keys(groupedResults)[0];

  const onUserVote = useCallback(() => {
    refetchPollResults();
    refetchUser();
  }, [refetchUser, refetchPollResults]);

  useEffect(() => {
    if (PollWeightTokenPublicKey) {
      const request = geTokenBalance({
        variables: {
          filter: {
            holder: {
              publicKey: {
                equalTo: currentUserPublicKey,
              },
            },
            creator: {
              publicKey: {
                equalTo: PollWeightTokenPublicKey,
              },
            },
            isDaoCoin: {
              equalTo: true,
            },
          },
        },
      });
      request.retain?.();
      void request.catch((error) => {
        if (!isAbortError(error)) {
          console.error("Failed to load token balances for poll", error);
        }
      });
    }
  }, [
    PollWeightTokenPublicKey,
    currentUserVote,
    geTokenBalance,
    currentUserPublicKey,
  ]);

  const isDataLoading =
    pollResultsLoading ||
    (isLoggedIn && !tokenBalancesData && PollWeightTokenPublicKey) ||
    (isLoggedIn && !user && pollType === PollWeightType.desoBalance) ||
    tokenBalancesLoading ||
    (isLoggedIn && userLoading);

  if (isDataLoading) {
    return (
      <div
        className="flex justify-center py-2"
        ref={pollContainerRef}
        style={{
          minHeight: pollHeight,
        }}
        data-cache-key={postCacheKey}
        data-track="poll"
      >
        <Spinner />
      </div>
    );
  }

  let parsedPollOptions: unknown = [];
  try {
    parsedPollOptions = JSON.parse(PollOptions);
  } catch (error) {
    console.error("Failed to parse PollOptions:", error);
    // Define your fallback logic here, for example, setting it to an empty array or a default value
    parsedPollOptions = []; // Fallback to an empty array or any default value you prefer
  }

  let additionalLabel = null;
  if (pollType === PollWeightType.desoBalance) {
    additionalLabel = `Weighted by ${DESO_APP_NAME} Balance`;
  } else if (pollType === PollWeightType.desoTokenBalance) {
    additionalLabel = `Weighted by ${DESO_TOKEN_NAME} Balance`;
  }

  const finalOptions = normalizePollOptions(parsedPollOptions);

  return (
    <StopPropagationWrapper
      ref={pollContainerRef}
      style={{
        minHeight: pollHeight,
      }}
      data-cache-key={postCacheKey}
      data-track="poll"
    >
      {additionalLabel && (
        <div className="mb-4 flex items-center justify-center text-xs text-muted">
          {additionalLabel}
        </div>
      )}
      {!currentUserVote && availableToVote && !showResultsOnly ? (
        <PostActivePoll options={finalOptions} onUserVote={onUserVote} />
      ) : (
        <PostPollResults
          {...{
            pollType,
            availableToVote,
            PollWeightTokenPublicKey,
            currentUserVote,
            options: finalOptions,
            results,
          }}
        />
      )}
      {results.length > 0 && (
        <div
          className="
            mt-2 flex items-center justify-center pr-4 text-right text-xs
            text-muted
          "
        >
          <div>
            <Button
              variant="ghost"
              className="
                flex items-center gap-1 p-0 text-xs font-normal text-muted
              "
              onClick={() => {
                setDialog(DIALOG.PollVoters, {
                  open: true,
                  params: {
                    postHash,
                    ...(defaultPollVotersTab
                      ? { tab: defaultPollVotersTab }
                      : {}),
                  },
                });
              }}
            >
              <span className="font-mono text-xs text-muted-foreground">
                {results.length}
              </span>{" "}
              total {pluralize(results.length, "vote", { includeCount: false })}
            </Button>
          </div>
          {!showResultsOnly && !currentUserVote && isCurrentUserAuthor && (
            <>
              <div className="mx-1">•</div>
              <Button
                variant="ghost"
                className="
                  flex items-center gap-2 p-0 text-xs font-normal text-primary
                  hover:underline
                "
                onClick={() => setShowResultsOnly(true)}
              >
                Show Results
              </Button>
            </>
          )}
        </div>
      )}
    </StopPropagationWrapper>
  );
};
